// src/components/VerCertificadoQRModal.js
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Spinner, Badge } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import { fetchCertificadoByCodigo } from '../actions/certificadoActions';
import { fetchSocioPhoto } from '../actions/socioActions';

import placeholder from '../assets/user-profile-photo.png';
import { getUploadUrl } from '../util/utilFunctions';

function Row({ label, value, bold }) {
  return (
    <div className="d-flex flex-column flex-sm-row align-items-start mb-2">
      <div className="text-muted qr-row-label me-sm-2">{label}:</div>
      <div className={bold ? 'fw-semibold' : ''}>{value ?? '—'}</div>
    </div>
  );
}

export default function VerCertificadoQRModal() {
  const { codigo } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { loading, error, data: cert } = useSelector((state) => state.certificado);

  const [show, setShow] = useState(true);
  const [imgSrc, setImgSrc] = useState(placeholder);

  // Traer certificado por código
  useEffect(() => {
    if (codigo) dispatch(fetchCertificadoByCodigo(codigo));
  }, [dispatch, codigo]);

  // Verificación/vigencia
  const verificacion = useMemo(() => {
    if (!cert) return null;

    if (cert.verificacion && typeof cert.verificacion === 'object') {
      return cert.verificacion;
    }

    const emision     = cert?.fecha_emision ? new Date(cert.fecha_emision) : null;
    const validoHasta = cert?.valido_hasta  ? new Date(cert.valido_hasta)  : null;
    let dias_restantes = null;
    let vigente = null;

    const hoy = new Date();
    if (validoHasta) {
      const ms = validoHasta.setHours(23, 59, 59, 999) - hoy.getTime();
      dias_restantes = Math.ceil(ms / (1000 * 60 * 60 * 24));
      vigente = ms >= 0;
    } else if (cert?.validez_dias && emision) {
      const hasta = new Date(emision);
      hasta.setDate(hasta.getDate() + Number(cert.validez_dias || 0));
      const ms = hasta.setHours(23, 59, 59, 999) - hoy.getTime();
      dias_restantes = Math.ceil(ms / (1000 * 60 * 60 * 24));
      vigente = ms >= 0;
    }

    return { vigente, dias_restantes, hoy: hoy.toISOString().slice(0, 10) };
  }, [cert]);

  // FOTO (igual a SocioForm)
  useEffect(() => {
    let canceled = false;

    (async () => {
      if (!cert) return;

      if (cert.foto) {
        const url = getUploadUrl(cert.foto);
        if (!canceled) setImgSrc(url || placeholder);
        return;
      }

      const socioId =
        cert?.id_socio ?? cert?.socio?.id ?? cert?.socio_id ?? cert?.socioId;

      if (socioId) {
        try {
          const fotoRawOrObj = await dispatch(fetchSocioPhoto(socioId));
          const fotoRaw =
            typeof fotoRawOrObj === 'string'
              ? fotoRawOrObj
              : (fotoRawOrObj && fotoRawOrObj.foto) ? fotoRawOrObj.foto : null;

          const url = fotoRaw ? getUploadUrl(fotoRaw) : null;
          if (!canceled) setImgSrc(url || placeholder);
          return;
        } catch {
          if (!canceled) setImgSrc(placeholder);
          return;
        }
      }

      if (!canceled) setImgSrc(placeholder);
    })();

    return () => { canceled = true; };
  }, [cert, dispatch]);

  const handleImgError = () => {
    if (imgSrc !== placeholder) setImgSrc(placeholder);
  };

  const handleClose = () => {
    setShow(false);
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  const emisionDate = cert?.fecha_emision ? new Date(cert.fecha_emision) : null;
  const hastaDate   = cert?.valido_hasta ? new Date(cert.valido_hasta) : null;

  return (
    <>
      {/* CSS mínimo para responsividad de etiquetas */}
      <style>{`
        @media (min-width: 576px) {
          .qr-row-label { min-width: 170px; }
        }
      `}</style>

      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        centered
        backdrop="static"
        keyboard={false}
        fullscreen="md-down"  /* Fullscreen en móviles/tablets chicas */
      >
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-5">Verificación de Certificado</Modal.Title>
        </Modal.Header>

        <Modal.Body className="pb-2">
          {/* Cabecera institucional */}
          <div className="text-center mb-3">
            <img
              src={process.env.PUBLIC_URL + '/assets/cabeceraciab.png'}
              alt="CIAB - Filial Tarija"
              className="img-fluid rounded"
              style={{ boxShadow: '0 6px 20px rgba(0,0,0,.08)', border: '1px solid #e5e7eb' }}
            />
          </div>

          {!codigo && (
            <div className="alert alert-warning mb-0">Código no provisto en la URL.</div>
          )}

          {codigo && loading && (
            <div className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              <span>Cargando…</span>
            </div>
          )}

          {codigo && !loading && error && (
            <div className="alert alert-danger mb-0">{error}</div>
          )}

          {codigo && !loading && !error && cert && (
            <div className="row g-4">
              {/* FOTO */}
              <div className="col-12 col-md-4">
                <div
                  className="w-100"
                  style={{
                    aspectRatio: '1 / 1',
                    borderRadius: 12,
                    overflow: 'hidden',
                    background: '#f3f4f6',
                    border: '1px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <img
                    src={imgSrc || placeholder}
                    alt="Foto del socio"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={handleImgError}
                  />
                </div>

                <div className="mt-2">
                  {verificacion?.vigente ? (
                    <Badge bg="success" className="rounded-pill">
                      VIGENTE {typeof verificacion.dias_restantes === 'number' ? `(${verificacion.dias_restantes} días restantes)` : ''}
                    </Badge>
                  ) : (
                    <Badge bg="danger" className="rounded-pill">VENCIDO</Badge>
                  )}
                </div>
              </div>

              {/* DETALLE */}
              <div className="col-12 col-md-8">
                <h4 className="mt-0 mb-2 fs-5">{cert?.tipo || 'Certificado'}</h4>
                <div className="text-muted mb-3">
                  Código: <strong>{cert?.codigo}</strong>
                </div>

                <div className="mb-3 p-3 border rounded-3">
                  <Row label="Entidad" value={cert?.entidad || 'CIAB - Filial Tarija'} />
                  <Row label="Fecha de emisión" value={emisionDate ? emisionDate.toLocaleDateString('es-BO') : '—'} />
                  <Row label="Válido hasta" value={hastaDate ? hastaDate.toLocaleDateString('es-BO') : (cert?.validez_dias ? `${cert?.validez_dias} días desde emisión` : '—')} />
                  {typeof verificacion?.dias_restantes === 'number' && (
                    <Row label="Días restantes" value={verificacion.dias_restantes} />
                  )}
                  {cert?.ciudad_emision && <Row label="Ciudad de emisión" value={cert.ciudad_emision} />}
                  {cert?.ley && <Row label="Base legal" value={cert.ley} />}
                </div>

                <h5 className="mt-0 fs-6">Datos del socio</h5>
                <div className="mb-3 p-3 border rounded-3">
                  <Row label="Apellidos" value={`${cert?.apPat || ''} ${cert?.apMat || ''}`.trim()} />
                  <Row label="Nombres" value={cert?.nombres} bold />
                  <Row label="C.I." value={cert?.ci} />
                  <Row label="Registro" value={cert?.codreg} />
                  <Row label="Grado" value={cert?.grado_texto || cert?.gradoTexto} />
                  <Row label="TPN" value={cert?.nrotit} />
                  <Row label="Universidad" value={cert?.suniv} />
                </div>

                {Array.isArray(cert?.titulos) && cert.titulos.length > 0 && (
                  <>
                    <h6 className="mt-0">Títulos</h6>
                    <div className="ps-2">
                      {cert.titulos.map((t, i) => (
                        <div key={i} className="mb-1">
                          • {t.titulo || '—'} {t.universidad ? `(${t.universidad})` : ''}{' '}
                          {t.nrotit ? `– TPN ${t.nrotit}` : ''} {t.fecha ? `– ${t.fecha}` : ''}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {cert?.texto_certificado && (
                  <>
                    <h6 className="mt-3">Detalle (Solvencia)</h6>
                    <div
                      style={{
                        whiteSpace: 'pre-line',
                        border: '1px dashed #e5e7eb',
                        padding: 12,
                        borderRadius: 8,
                        background: '#fafafa'
                      }}
                    >
                      {cert.texto_certificado}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="py-2">
          <Button variant="secondary" onClick={handleClose}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
